import { useHistory } from 'react-router-dom';
import cn from 'clsx';

import ProxyIcon from '@images/proxy.svg';
import PollIcon from '@images/withdraw-poll.svg';
import VoteIcon from '@images/withdraw-vote.svg';
import RepresentativeIcon from '@images/representative.svg';
import ActionIcon from '@images/action-icon.svg';
import { useAuth } from '@hooks/Auth';
import { CompanyRole, Tier, UserType } from '@sdk/API';
import { DashboardIcon } from '@components/elements/Icons';

interface AdminActionCardProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  icon: React.ReactNode;
  disabled?: boolean;
}

const AdminActionCard: React.FC<AdminActionCardProps> = ({
  title,
  icon,
  disabled,
  onClick,
  ...props
}) => {
  return (
    <div
      role="button"
      aria-disabled={disabled}
      className={cn('flex bg-white select-none shadow-r-magenta-md', {
        'cursor-not-allowed': disabled,
      })}
      onClick={disabled ? undefined : onClick}
      {...props}
    >
      <div className="flex items-center p-4 bg-orange md:p-8">
        {typeof icon === 'string' ? (
          <img src={icon} alt={title} className="w-8" />
        ) : (
          icon
        )}
      </div>
      <div className="p-4 md:p-8">
        <h2 className="text-xl capitalize md:text-3xl">{title}</h2>
      </div>
    </div>
  );
};

const AdminActionsPage: React.FC = () => {
  const history = useHistory<any>();
  const { dbUser, company, loading } = useAuth();

  const isAdmin =
    dbUser?.type === UserType.ADMINISTRATOR ||
    company?.role === CompanyRole.ADMIN ||
    company?.role === CompanyRole.REPRESENTATIVE;
  const requireSetup =
    loading === false && dbUser?.tier === Tier.ENTERPRISE && !company;

  const cardContainerCn = 'w-full xl:max-w-lg';
  return (
    <div className="flex min-h-screen px-4 pb-32 bg-blue-light">
      <div className="w-full pt-16 space-y-4">
        <div className={cardContainerCn}>
          <AdminActionCard
            icon={ActionIcon}
            title="Create Poll"
            onClick={() => history.push('/poll/create')}
          />
        </div>
        <div className={cardContainerCn}>
          <AdminActionCard
            icon={ActionIcon}
            title="Create pledge"
            onClick={() => history.push('/poll/pledge')}
          />
        </div>
        {!requireSetup && (
          <div className={cardContainerCn}>
            <AdminActionCard
              icon={<DashboardIcon className="w-8 h-8" />}
              title="Company Dashboard"
              onClick={() => history.push('/admin/dashboard')}
            />
          </div>
        )}
        {!requireSetup && (
          <div className={cardContainerCn}>
            <AdminActionCard
              icon={PollIcon}
              title="Manage Company Polls"
              onClick={() => history.push('/admin/polls')}
            />
          </div>
        )}
        {!requireSetup && (
          <div className={cardContainerCn}>
            <AdminActionCard
              icon={RepresentativeIcon}
              title="Company Members"
              onClick={() => history.push('/admin/members')}
            />
          </div>
        )}
        {!requireSetup && isAdmin && (
          <div className={cardContainerCn}>
            <AdminActionCard
              icon={RepresentativeIcon}
              title="Assign Company Representative"
              onClick={() => history.push('/admin/representatives')}
            />
          </div>
        )}
        {!requireSetup && (
          <div className={cardContainerCn}>
            <AdminActionCard
              icon={ProxyIcon}
              title="Assign Company Proxy"
              onClick={() => history.push('/admin/proxy')}
            />
          </div>
        )}
        {!requireSetup && (
          <div className={cardContainerCn}>
            <AdminActionCard
              icon={VoteIcon}
              title="Withdraw Company Poll Votes"
              onClick={() => history.push('/admin/votes')}
            />
          </div>
        )}
        {!requireSetup && (
          <div className={cardContainerCn}>
            <AdminActionCard
              icon={ProxyIcon}
              title="Manage Company Settings"
              onClick={() => history.push('/company/settings')}
            />
          </div>
        )}
        {requireSetup && (
          <div className={cardContainerCn}>
            <AdminActionCard
              icon={ProxyIcon}
              title="Setup Company"
              onClick={() => history.push('/company/setup')}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminActionsPage;
